import React, { useCallback, useEffect, useRef, useState } from 'react'
import useDebounce from 'CustomHooks/useDebounce';
import api from 'http';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';
import PropTypes from 'prop-types';
import CustomLabel from 'Common/CustomLabel/CustomLabel';
import { withFeatureSafe } from 'Common/ErrorBoundaries';

const AsyncSelect = ({
    id,
    wrapperClass,
    label,
    labelClass,
    extraClass,
    required,
    onChange,
    apiLink,
    disabled,
    name,
    getData,
    formatLabel,
    placeholder,
    options,
    ...props
}) => {
    const [data, setData] = useState([]);
    const [isChanging, setIsChanging] = useState(false);
    const [clickCount, setClickCount] = useState(0); // Track click count for debouncing
    const [isDisabled, setIsDisabled] = useState(true)
    const hasFetched = useRef(false);
    const debouncedClickCount = useDebounce(clickCount, 500); // 500ms delay
    const resolvedOptions = options ?? data;


    const handleChange = (event) => {
        setIsChanging(true);
        onChange(event)
        setTimeout(() => setIsChanging(false), 0); // Reset the `isChanging` flag after a small delay
    }

    const handleClick = () => {
        if (isChanging) return; // Prevent click logic during change
        setClickCount((prev) => prev + 1);  // Increment the click count to trigger debounce
    };
    // Function to fetch providers (memoized with useCallback)
    const fetchProviders = useCallback(async () => {
        if (options) {
            return;
        }
        try {
            const resp = await api.get(apiLink);
            setData(resp.data.dataModel);

        } catch (error) {
            ErrorHandlingMessage(error);
        }
    }, [apiLink, options]); // Empty dependency ensures function identity remains stable

    // Make the API call whenever the debounced value changes
    useEffect(() => {
        if (options) {
            return;
        }
        // Only make the API call if clickCount changes (debounced)
        if (debouncedClickCount > 0) {
            fetchProviders()
        }
    }, [fetchProviders, debouncedClickCount, options]);

    // Trigger API on component mount (first load)
    useEffect(() => {
        if (options) {
            return;
        }
        if (hasFetched.current) return;
        hasFetched.current = true;

        (async () => {
            try {
                const resp = await api.get(apiLink);
                setData(resp.data.dataModel);
                getData?.(resp?.data?.dataModel ?? []);
            } catch (error) {
                ErrorHandlingMessage(error);
            } finally {
                setIsDisabled(false);
            }
        })();
    }, [apiLink, getData, options]);

    return (


        <div className={`flex flex-col gap-2  w-full ${wrapperClass}`}>
            {label && <CustomLabel htmlFor={id} label={label} labelClass={labelClass} required={required} />}
            <select
                className="customInputClass h-9"
                style={{ paddingRight: '0.5rem', textOverflow: 'ellipsis' }}
                onClick={handleClick} name={name} disabled={disabled || (options ? false : isDisabled)} onChange={handleChange}
                {...props}
            >
               {placeholder && <option value="" className="dropdownItem">{placeholder || "Please Select"}</option>}
                {resolvedOptions?.map((d) => (
                    <option value={d.value ?? d.id} key={d.id ?? d.value} className="dropdownItem">
                        {formatLabel ? formatLabel(d) : d.label}
                    </option>
                ))}
            </select>
        </div>
    )
}

export default withFeatureSafe(AsyncSelect)

AsyncSelect.propTypes = {
    id: PropTypes.string,
    wrapperClass: PropTypes.string,
    label: PropTypes.string,
    labelClass: PropTypes.string,
    extraClass: PropTypes.string,
    required: PropTypes.bool,
    onChange: PropTypes.func,
    apiLink: PropTypes.string,
    disabled: PropTypes.bool,
    name: PropTypes.string,
    formatLabel: PropTypes.func,
    placeholder: PropTypes.string,
    options: PropTypes.array
};